import type { ComponentProps } from "react";
import type { RegionSelectContentFragment } from "../client/generated";
import type { AttributeKind } from "../types/utils";
import { useTranslation } from "react-i18next";
import { RegionThumbnail } from "./RegionThumbnail";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
} from "@acdc2/ui/components/select";

type Props = ComponentProps<typeof Select> & {
  fragment: RegionSelectContentFragment;
  qualifyingAttributes: AttributeKind[];
  currentPageGroupRef?: React.RefObject<HTMLDivElement | null>;
};

export function RegionSelectContent({
  fragment,
  qualifyingAttributes,
  currentPageGroupRef,
}: Props) {
  const { t } = useTranslation();

  return (
    <SelectContent>
      {fragment.document.pages.pages.map((page) => {
        const selectableRegions = page.regions.filter((region) =>
          region.attributes.some(
            (attr) =>
              attr.__typename && qualifyingAttributes.includes(attr.__typename),
          ),
        );

        if (selectableRegions.length === 0) {
          return null;
        }

        return (
          <SelectGroup
            key={page.id}
            ref={fragment.page.id === page.id ? currentPageGroupRef : undefined}
          >
            <SelectLabel>
              {fragment.page.id === page.id
                ? t("RegionSelectContent.groupLabel.current", {
                    page: page.index,
                  })
                : t("RegionSelectContent.groupLabel.default", {
                    page: page.index,
                  })}
            </SelectLabel>
            {selectableRegions.map((region) => (
              <SelectItem
                key={region.id}
                value={region.id}
                className="flex-col justify-center items-stretch"
              >
                <RegionThumbnail fragment={region} className="w-full" />
              </SelectItem>
            ))}
          </SelectGroup>
        );
      })}
    </SelectContent>
  );
}
